import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './index.scss';

const PasswordlessSignIn = ({ onSuccess, title }) => {
  const { t } = useTranslation();
  const { sendMagicLink } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [linkSent, setLinkSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    if (!email || !email.includes('@')) {
      setError(t('passwordlessSignInInvalidEmail') || 'Please enter a valid email address');
      setLoading(false);
      return;
    }

    try {
      const result = await sendMagicLink(email);
      
      if (result.success) {
        setLinkSent(true);
        setMessage(
          t('passwordlessSignInLinkSent') || 
          `A sign-in link has been sent to ${email}. Please check your email and click the link to access the page.`
        );
      } else {
        setError(result.error || (t('passwordlessSignInError') || 'Failed to send sign-in link'));
      }
    } catch (err) {
      setError(t('passwordlessSignInError') || 'Failed to send sign-in link');
    } finally {
      setLoading(false);
    }
  };

  const handleResendLink = async () => {
    setLoading(true);
    setError('');
    
    try {
      const result = await sendMagicLink(email);
      
      if (result.success) {
        setMessage(
          t('passwordlessSignInLinkResent') || 
          'Sign-in link has been resent to your email.'
        );
      } else {
        setError(result.error || (t('passwordlessSignInError') || 'Failed to send sign-in link'));
      }
    } catch (err) {
      setError(t('passwordlessSignInError') || 'Failed to send sign-in link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="passwordless-signin-container">
      <div className="signin-card">
        <h2>{title || (t('passwordlessSignInTitle') || 'Secure Access Required')}</h2>
        <p className="signin-description">
          {t('passwordlessSignInDescription') || 
           'To protect sensitive donation information, please verify your email address. We\'ll send you a secure link to access this page.'}
        </p>

        {!linkSent ? (
          <form onSubmit={handleSubmit} className="signin-form">
            <div className="form-group">
              <label htmlFor="email">
                {t('passwordlessSignInEmailLabel') || 'Email Address'}
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('passwordlessSignInEmailPlaceholder') || 'Enter your email address'}
                required
                disabled={loading}
                className="email-input"
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button 
              type="submit" 
              disabled={loading || !email}
              className="signin-button"
            >
              {loading 
                ? (t('passwordlessSignInSending') || 'Sending...') 
                : (t('passwordlessSignInSendLink') || 'Send Secure Link')
              }
            </button>
          </form>
        ) : (
          <div className="link-sent-container">
            <div className="success-icon">✉️</div>
            {message && <div className="success-message">{message}</div>}
            
            <div className="link-sent-actions">
              <p className="resend-text">
                {t('passwordlessSignInDidntReceive') || "Didn't receive the email?"}
              </p>
              <button 
                onClick={handleResendLink}
                disabled={loading}
                className="resend-button"
              >
                {loading 
                  ? (t('passwordlessSignInSending') || 'Sending...') 
                  : (t('passwordlessSignInResendLink') || 'Resend Link')
                }
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}
          </div>
        )}

        <div className="security-note">
          <small>
            🔒 {t('passwordlessSignInSecurityNote') || 
            'This secure authentication method protects your donation information without requiring passwords.'}
          </small>
        </div>
      </div>
    </div>
  );
};

export default PasswordlessSignIn;
